'use client';

/**
 * PageContentHeader Component
 *
 * Large in-content title block inspired by Asana/ClickUp.
 * Displays title, description, icon, inline actions (favorite, status, dropdown),
 * badges, action buttons, and an optional tab bar below.
 *
 * Rendered inside PageTemplate above the page content, replacing the
 * title that was previously shown in AppNavBar.
 *
 * @see PR #856 — Navigation & Layout Redesign
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StarFilledIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarBorder';
import { Box, IconButton, Menu, Typography, useTheme } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { NavTab, VisibilityContext } from '@ops-matrix/shared-types';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { GlassTooltip } from '../GlassTooltip';
import { PageTabBar } from '../PageTabBar';

// ============================================================================
// Types
// ============================================================================

export interface PageContentHeaderProps {
  /** Page title */
  title?: string;
  /** Page description — accepts a string (default) or a ReactNode when
   *  the page needs inline-tinted segments (e.g. the dashboard's
   *  "0 at SLA risk" amber pill inside a longer subtitle). */
  description?: React.ReactNode;
  /** Icon component displayed before the title */
  icon?: React.ComponentType<SvgIconProps>;
  /** Back navigation handler — shows a back arrow when set */
  onBack?: () => void;

  /** Favorite toggle handler */
  onFavorite?: () => void;
  /** Whether the page is currently favorited */
  isFavorited?: boolean;
  /** Status chip rendered inline after the title */
  statusChip?: React.ReactNode;
  /** Dropdown menu items for the chevron button */
  titleMenuItems?: React.ReactNode;

  /** Badge/chip elements displayed after the description */
  badges?: React.ReactNode;
  /** Right-aligned action buttons */
  actions?: React.ReactNode;

  /** Tab bar items */
  tabs?: NavTab[];
  /** Currently active tab ID */
  activeTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string, path: string) => void;
  /** Visibility context for filtering tabs */
  visibilityContext?: VisibilityContext;

  /**
   * Optional content rendered between the description+badges row and the
   * tab bar — typically a per-page chrome row like a stage indicator,
   * progress strip, or filter band. Lives INSIDE the sticky surface so it
   * stays anchored with the title and tabs while the body scrolls.
   *
   * Use sparingly — over-stuffing the header makes operators lose vertical
   * real estate on every page. Reserve for content that is genuinely the
   * "page chrome" rather than top-of-content.
   */
  subHeader?: React.ReactNode;

  /**
   * Title + tabs stick to the top of the scroll container as the page body
   * scrolls — operators always see what page / record they're on. The header
   * gets the surface background + a backdrop blur so body content reads
   * underneath without obscuring the header.
   *
   * **Default true.** Pass `sticky={false}` only to opt out (e.g. tiny
   * vertical surfaces where the header overlaying the body would be
   * confusing).
   */
  sticky?: boolean;
  /**
   * Top offset (px) for the sticky position. Use when an upstream chrome
   * element (banner, app bar) needs to remain visible above the sticky
   * header. Default 0.
   */
  stickyTop?: number;
}

// ============================================================================
// Component
// ============================================================================

export const PageContentHeader: React.FC<PageContentHeaderProps> = ({
  title,
  description,
  icon: Icon,
  onBack,
  onFavorite,
  isFavorited = false,
  statusChip,
  titleMenuItems,
  badges,
  actions,
  tabs,
  activeTab,
  onTabChange,
  visibilityContext,
  subHeader,
  sticky = true,
  stickyTop = 0,
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const hasTabs = tabs && tabs.length > 1;
  const hasTitle = title || Icon || description;
  const hasSubHeader = Boolean(subHeader);

  // Publish the rendered header height as `--page-header-height` so siblings
  // (e.g. ContextRail's panel header) can size themselves to match — keeps
  // the page header bottom border aligned with the rail header bottom border.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof document === 'undefined') return;
    const root = document.documentElement;
    const write = () => {
      root.style.setProperty('--page-header-height', `${Math.round(node.getBoundingClientRect().height)}px`);
    };
    write();
    const ro = new ResizeObserver(write);
    ro.observe(node);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--page-header-height');
    };
  }, []);

  if (!hasTitle && !hasTabs && !hasSubHeader) return null;

  return (
    <Box
      ref={rootRef}
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: { xs: 2, sm: 3 },
        ...(sticky && {
          position: 'sticky',
          top: stickyTop,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: theme.palette.background.default,
          // Subtle backdrop blur so body content reads underneath without obscuring the header.
          backdropFilter: 'saturate(150%) blur(10px)',
          WebkitBackdropFilter: 'saturate(150%) blur(10px)',
        }),
      }}
    >
      {/* Title Row — title (left) + tabs (center/right inline) + actions (far right).
          Tabs ride INSIDE the header band so the page chrome is a single compact row,
          not a header-then-tabs stack. On narrow viewports (xs) tabs wrap to their
          own row below the title via `flexWrap: 'wrap'` — they keep their tablist
          semantics and stay inside the sticky surface in both layouts. */}
      {hasTitle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: 1.5,
            rowGap: 0.5,
            pt: 1.5,
            pb: description || badges ? 0.5 : 2,
          }}
        >
          {/* Left: Back + Icon + Title + Inline Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              // When tabs are inline, the title cluster shrinks to its content so
              // tabs can take adjacent room. Without tabs, the title cluster
              // expands to push actions to the far right (preserves old layout).
              flex: hasTabs ? '0 1 auto' : 1,
              minWidth: 0,
            }}
          >
            {/* Back Arrow */}
            {onBack && (
              <IconButton
                size="small"
                onClick={onBack}
                sx={{ color: 'text.secondary', ml: -0.5 }}
                aria-label={t('common.goBack')}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}

            {/* Icon */}
            {Icon && (
              <Icon
                sx={{
                  fontSize: 28, // Off-scale: between lg (24) and xl (32)
                  color: 'primary.main',
                  flexShrink: 0,
                }}
              />
            )}

            {/* Title */}
            {title && (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {title}
              </Typography>
            )}

            {/* Favorite Star */}
            {onFavorite && (
              <GlassTooltip title={isFavorited ? t('common.unfavorite') : t('common.favorite')}>
                <IconButton
                  size="small"
                  onClick={onFavorite}
                  sx={{
                    color: isFavorited ? 'warning.main' : 'text.disabled',
                    flexShrink: 0,
                  }}
                  aria-label={isFavorited ? t('common.unfavorite') : t('common.favorite')}
                >
                  {isFavorited ? <StarFilledIcon fontSize="small" /> : <StarOutlineIcon fontSize="small" />}
                </IconButton>
              </GlassTooltip>
            )}

            {/* Status Chip */}
            {statusChip}

            {/* Chevron Dropdown */}
            {titleMenuItems && (
              <>
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{ color: 'text.secondary', flexShrink: 0 }}
                  aria-label={t('common.more')}
                >
                  <ChevronDownIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                  transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                  slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
                  onClick={handleMenuClose}
                >
                  {titleMenuItems}
                </Menu>
              </>
            )}
          </Box>

          {/* Center: Inline tab bar — slots between the title cluster and the
              actions cluster. Flexes to fill remaining space at desktop widths;
              wraps to a fresh row at xs via parent `flexWrap`. Tab bar keeps its
              own scroll handling for overflow, so very-long tab lists (10+
              entries on loan detail) stay reachable. */}
          {hasTabs && activeTab && onTabChange && (
            <Box
              sx={{
                // At xs the row wraps and tabs claim the full width below the
                // title; from sm up they share the row and flex into the gap
                // before the actions cluster.
                flex: { xs: '1 0 100%', sm: '1 1 auto' },
                minWidth: 0,
                // PageTabBar paints its own baseline rule under tabs (the
                // continuous low-contrast bottom border); we let the header's
                // own borderBottom carry the band's outer rule so we don't
                // stack two rules.
                ml: { xs: 0, sm: 'auto' },
              }}
            >
              <PageTabBar
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={onTabChange}
                visibilityContext={visibilityContext}
                sticky={false}
                embedded
                size="small"
              />
            </Box>
          )}

          {/* Right: Actions */}
          {actions && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
                // Push actions to the far right at every breakpoint. When
                // tabs ARE inline:
                //   - sm+: the tab block has `ml: 'auto'`, so it claims the
                //     slack and actions sit flush right of it.
                //   - xs (tabs wrap to their own row): without an `ml: 'auto'`
                //     here, actions would render flush against the title
                //     cluster instead of right-aligned. Setting `ml: 'auto'`
                //     on xs keeps actions pinned to the right of the title
                //     row before tabs wrap below.
                // When tabs are NOT inline, `ml: 'auto'` at all breakpoints
                // preserves the legacy behavior.
                ml: 'auto',
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      )}
      {/* Description + Badges Row */}
      {(description || badges) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 2,
            pl: onBack ? 5 : Icon ? 5 : 0,
          }}
        >
          {description && (
            // component="div" — description may be a ReactNode (e.g. chips),
            // not just a string; <p> can't contain block-level children.
            <Typography
              variant="body2"
              component="div"
              noWrap
              sx={{
                color: 'text.secondary',
                minWidth: 0,
              }}
            >
              {description}
            </Typography>
          )}
          {badges}
        </Box>
      )}
      {/* Sub-header slot — per-page chrome (e.g. stage indicator). Renders
          between the description/badges row and the tab bar so it stays inside
          the sticky surface and anchors with the page title. */}
      {subHeader && <Box sx={{ pt: hasTitle ? 1 : 1.5, pb: 1.5 }}>{subHeader}</Box>}
      {/* Fallback Tab Bar — only renders when there is NO title row (rare;
          tabs without a page title). Title-row tabs already render inline
          above; this branch keeps the historical "header is tabs-only" shape
          working for any consumer that opts in. */}
      {hasTabs && activeTab && onTabChange && !hasTitle && (
        <Box sx={{ mx: { xs: -2, sm: -3 } }}>
          <PageTabBar
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
            visibilityContext={visibilityContext}
            sticky={false}
            embedded
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default PageContentHeader;
